import axios from 'axios';
import { ShopperEventDto, EventType } from '@luckycart/xpn-types';
import { datadogLogs } from '@datadog/browser-logs';

export class EventService {
  private readonly hostShopperEvent: string;

  public constructor() {
    this.hostShopperEvent = process.env
      .REACT_APP_SHOPPER_EVENT_BASE_URL as string;
  }

  /**
   * send an event to save the shopper email
   * @param {string} siteKey
   * @param {string} shopperId
   * @param {string} experienceId
   * @param {string} shopperEmail
   * @returns {Promise<void>}
   */
  public sendEmailEventToShopperEvent(
    siteKey: string,
    shopperId: string,
    experienceId: string,
    shopperEmail: string
  ): Promise<void> {
    const event: ShopperEventDto = {
      eventName: EventType.SHOPPER_EMAIL_SAVED,
      shopperId,
      siteKey,
      payload: {
        shopperEmail,
        experienceId,
      },
    };

    return this.sendEvent(event);
  }

  public sendOptinViewedEventToShopperEvent(
    siteKey: string,
    shopperId: string,
    experienceId: string,
    optinKey: string
  ): Promise<void> {
    const event: ShopperEventDto = {
      eventName: EventType.SMART_OPTINS_VIEWED,
      shopperId,
      siteKey,
      payload: {
        experienceId,
        optinKey,
      },
    };

    return this.sendEvent(event);
  }

  public sendOptinAcceptedEventToShopperEvent(
    siteKey: string,
    shopperId: string,
    experienceId: string,
    optinKey: string
  ): Promise<void> {
    const event: ShopperEventDto = {
      eventName: EventType.OPTIN_ACCEPTED,
      shopperId,
      siteKey,
      payload: {
        optinKey,
        experienceId,
      },
    };

    return this.sendEvent(event);
  }

  public sendOptinRefusedEventToShopperEvent(
    siteKey: string,
    shopperId: string,
    experienceId: string,
    optinKey: string
  ): Promise<void> {
    const event: ShopperEventDto = {
      eventName: EventType.OPTIN_REFUSED,
      shopperId,
      siteKey,
      payload: {
        optinKey,
        experienceId,
      },
    };

    return this.sendEvent(event);
  }

 
  public sendPopinViewedEventToShopperEvent({
    siteKey,
    shopperId,
    cartId,
    experienceId,
    operationId,
  }: {
    siteKey: string;
    shopperId: string;
    cartId: string;
    experienceId: string;
    operationId: string;
  }): Promise<void> {
    const event: ShopperEventDto = {
      eventName: EventType.POPIN_VIEWED,
      shopperId,
      siteKey,
      payload: {
        cartId,
        experienceId,
        operationId,
      },
    };

    return this.sendEvent(event);
  }

  public sendGameViewedEventToShopperEvent({
    siteKey,
    shopperId,
    cartId,
    experienceId,
    operationId,
  }: {
    siteKey: string;
    shopperId: string;
    cartId: string;
    experienceId: string;
    operationId: string;
  }): Promise<void> {
    const event: ShopperEventDto = {
      eventName: EventType.GAME_VIEWED,
      shopperId,
      siteKey,
      payload: {
        cartId,
        experienceId,
        operationId,
      },
    };

    return this.sendEvent(event);
  }

  /**
   * Send an event to XPN
   * @param {Record<string, unknown>} data - event summary
   * @param {Record<string, unknown>} payload - event payload
   * @returns {Promise<void>}
   */
  private async sendEvent(event: ShopperEventDto): Promise<void> {
    const eventUrl = `${this.hostShopperEvent}/v1/event`;

    await axios
      .post(eventUrl, event)
      .catch((err) => {
        datadogLogs.logger.error(
          `Couldn't send the XPN event ${event.eventName}`,
          {
            err,
            event,
            shopperId: event.shopperId,
          }
        );
      });
  }
}

export const eventServiceInstance = new EventService();
